import React, { useState } from 'react';

const TokenImageUpload = ({ currentImage, onImageChange }) => {
  const [preview, setPreview] = useState(currentImage || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, WebP, or SVG)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    // Create FormData for upload
    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload-token-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      
      // For uploaded files, set preview to the returned path
      setPreview(data.imagePath);
      
      // Pass the path to parent component (store this in DB)
      onImageChange(data.imagePath);
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlInput = (e) => {
    const url = e.target.value;
    setPreview(url);
    onImageChange(url);
  };

  const isImageUrl = (str) => {
    try {
      const url = new URL(str);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const getDisplayText = (text) => {
    if (!text) return '';
    
    // If it's a file path, show just the filename
    if (text.includes('/') && !text.startsWith('http')) {
      const parts = text.split('/');
      return parts[parts.length - 1];
    }
    
    // If it's a long URL, truncate it
    if (text.length > 50 && text.startsWith('http')) {
      return text.substring(0, 30) + '...' + text.substring(text.length - 15);
    }
    
    return text;
  };

  const displayImage = () => {
    if (!preview) return null;
    
    // Check if it's a URL or an emoji/icon
    if (isImageUrl(preview) || preview.startsWith('/token-images/')) {
      const imageUrl = preview.startsWith('/token-images/') 
        ? `${process.env.REACT_APP_API_URL}${preview}`
        : preview;
      
      return (
        <img 
          src={imageUrl} 
          alt="Token" 
          className="w-16 h-16 object-cover rounded-lg"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    // Display as emoji/icon
    return (
      <div className="w-16 h-16 flex items-center justify-center text-3xl bg-gray-100 dark:bg-gray-700 rounded-lg">
        {preview}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4">
        {preview ? (
          <div className="relative">
            {displayImage()}
            <div 
              className="w-16 h-16 hidden items-center justify-center text-3xl bg-gray-100 dark:bg-gray-700 rounded-lg"
              style={{ display: 'none' }}
            >
              {preview}
            </div>
          </div>
        ) : (
          <div className="w-16 h-16 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <span className="text-gray-400">No image</span>
          </div>
        )}
        
        <div className="flex-1">
          <label className="block text-sm font-medium mb-2">
            Upload Image or Enter URL/Emoji
          </label>
          
          <div className="flex space-x-2">
            <label className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                className="hidden"
              />
              <button
                type="button"
                onClick={(e) => e.currentTarget.previousSibling.click()}
                disabled={uploading}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Choose File'}
              </button>
            </label>
            
            <input
              type="text"
              placeholder="Or enter URL/emoji"
              value={preview}
              onChange={handleUrlInput}
              className="flex-1 px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm min-w-0"
              title={preview} // Show full text on hover
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }}
            />
          </div>
        </div>
      </div>
      
      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
      
      {preview && preview.length > 30 && (
        <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
          <strong>Current:</strong> {getDisplayText(preview)}
          {preview.startsWith('http') && <span className="ml-2 text-blue-500">(External URL)</span>}
          {preview.startsWith('/token-images/') && <span className="ml-2 text-green-500">(Uploaded file)</span>}
        </div>
      )}
      
      <p className="text-xs text-gray-500 dark:text-gray-400">
        You can upload an image file (max 5MB), enter an image URL, or use an emoji icon
      </p>
    </div>
  );
};

export default TokenImageUpload;